import { Report, ReportType } from '@/api/generatedApi';
import { ReportStatusCell } from '@/components/Report/ReportStatusCell';
import { ReportDetailsCell } from '@/components/Report/ReportDetailsCell';
import { REPORT_TITLES } from '@/components/Report/consts';
import { getReportCreatorLabel } from '@/components/Report/getReportCreatorLabel';
import { useTenants } from '@/services/tenants/useTenants';
import { Box, styled } from '@mui/material';
import {
  ContentCopy as DuplicationIcon,
  TrendingDown as SimplificationIcon,
  Tune as CustomIcon,
} from '@mui/icons-material';
import { GridRenderCellParams } from '@mui/x-data-grid-pro';
import { formatDistanceToNow } from 'date-fns';
import { Link } from 'react-router-dom';
import { Ellipsis } from '../Ellipsis';
import { pluralize } from '@/utils/format';

const StyledLink = styled(Link)(({ theme }) => ({
  color: theme.palette.text.primary,
  textDecoration: 'none',
  cursor: 'pointer',
  width: '100%',
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  '&:hover': {
    textDecoration: 'underline',
  },
}));

const getReportTypeIcon = (reportType: ReportType) => {
  switch (reportType) {
    case ReportType.DUPLICATION: {
      return <DuplicationIcon sx={{ fontSize: '18px', mr: 1 }} />;
    }
    case ReportType.SIMPLIFICATION: {
      return <SimplificationIcon sx={{ fontSize: '18px', mr: 1 }} />;
    }
    case ReportType.CUSTOM: {
      return <CustomIcon sx={{ fontSize: '18px', mr: 1 }} />;
    }
  }
};

export const RenderReportName = (parameters: GridRenderCellParams<Report>) => {
  const { getPath } = useTenants();

  return (
    <StyledLink to={getPath(`report/${parameters.row.id}`)}>
      {getReportTypeIcon(parameters.row.reportType)}
      <Ellipsis text={parameters.row.name || REPORT_TITLES[parameters.row.reportType]} />
    </StyledLink>
  );
};

export const RenderReportDetails = (parameters: GridRenderCellParams<Report>) => {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        height: '100%',
        lineHeight: 'normal',
      }}
    >
      <ReportDetailsCell report={parameters.row} noWrap />
    </Box>
  );
};

export const RenderReportStatus = (parameters: GridRenderCellParams<Report>) => {
  return <ReportStatusCell row={parameters.row} />;
};

export const RenderReportCreatedBy = (parameters: GridRenderCellParams<Report>) => {
  return getReportCreatorLabel(parameters.row);
};

export const RenderReportCreatedAt = (parameters: GridRenderCellParams<Report>) => {
  return parameters.value ? formatDistanceToNow(new Date(parameters.value as string), { addSuffix: true }) : '';
};

export const RenderReportGroups = (parameters: GridRenderCellParams<Report>) => {
  const groupCount = parameters.row.groupCount ?? 0;
  return pluralize(groupCount, 'Group', 'Groups');
};
